import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Popover,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
// One tint per meta-status card, cycling (display only).
const TONES = [
  { tint: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF", dot: "#6E5FE0" },
  { tint: "#E6F6F2", edge: "#BFE6DC", ink: "#146B58", dot: "#1FA386" },
  { tint: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08", dot: "#E0A21B" },
  { tint: "#FCEBF3", edge: "#F3CADD", ink: "#9C2B63", dot: "#DB4B8E" },
  { tint: "#E9F7EE", edge: "#C4E8D2", ink: "#1B6B41", dot: "#2FA864" },
  { tint: "#E6F5FA", edge: "#BEE3F0", ink: "#0E6380", dot: "#1E9CC7" },
  { tint: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A", dot: "#E0564A" },
];

const MarketerDashboard = ({ projectId }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));

  const [loading, setLoading] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);
  const [metaStatusKpis, setMetaStatusKpis] = useState([]);
  const [trend, setTrend] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);

  const theme = useTheme();
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("sm"));
  const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const isTouch = isMobileWidth || isCoarsePointer;
  const orgId = JSON.parse(localStorage.getItem("org"));

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead/analytics?projectId=${projectId}&orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          }
        );

        setTotalLeads(res.data.totalLeads);
        setMetaStatusKpis(res.data.metaStatusKpis || []);
        setTrend(res.data.trend || []);
      } catch (error) {
        console.error("Failed to fetch lead analytics", error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId && storedUser?.accessToken) {
      fetchAnalytics();
    }
  }, [projectId, storedUser?.accessToken]);

  const metaStatusData = useMemo(() => {
    const map = {};

    metaStatusKpis.forEach((kpi) => {
      if (!map[kpi.metaStatus]) {
        map[kpi.metaStatus] = {
          total: 0,
          statuses: [],
        };
      }

      map[kpi.metaStatus].total += kpi.count;
      map[kpi.metaStatus].statuses.push({
        status: kpi.leadStatus,
        count: kpi.count,
      });
    });

    return map;
  }, [metaStatusKpis]);

  const openMeta = (e, meta) => {
    setAnchorEl(e.currentTarget);
    setActiveMeta(meta);
  };

  const closeMeta = () => {
    setAnchorEl(null);
    setActiveMeta(null);
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress size={28} sx={{ color: "#0B5C8A" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 0.5 }, fontFamily: FONT, color: INK }}>
      <Typography
        variant="h5"
        mb={2.5}
        sx={{ fontFamily: FONT, fontSize: 27, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}
      >
        Lead Analytics
      </Typography>

      {/* KPI CARDS */}
      <Grid container spacing={1.5} mb={2.5}>
        {/* TOTAL */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...cardStyle, background: "#EAF2FB", borderColor: "#C9DEF3" }}>
            <CardContent sx={cardContentSx}>
              <Typography variant="caption" sx={{ ...capSx, color: "#1B4F82" }}>
                Total Leads
              </Typography>
              <Typography variant="h5" sx={{ ...numSx, color: "#1B4F82" }}>
                {totalLeads}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* META STATUS CARDS */}
        {Object.entries(metaStatusData).map(([meta, data], i) => {
          const t = TONES[i % TONES.length];
          return (
          <Grid item xs={12} sm={6} md={3} key={meta}>
            <Card
              sx={{ ...cardStyle, cursor: "pointer", background: t.tint, borderColor: t.edge }}
              onMouseEnter={!isTouch ? (e) => openMeta(e, meta) : undefined}
              onMouseLeave={!isTouch ? closeMeta : undefined}
              onClick={isTouch ? (e) => openMeta(e, meta) : undefined}
            >
              <CardContent sx={cardContentSx}>
                <Typography variant="caption" sx={{ ...capSx, color: t.ink, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flex: "0 0 7px" }}></span>
                  {meta}
                </Typography>
                <Typography variant="h5" sx={{ ...numSx, color: t.ink }}>
                  {data.total}
                </Typography>
              </CardContent>

              {/* POPOVER */}
              <Popover
                open={activeMeta === meta}
                anchorEl={anchorEl}
                onClose={closeMeta}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                disableRestoreFocus
                sx={{ pointerEvents: isTouch ? "auto" : "none" }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.75,
                      borderRadius: "12px",
                      border: `1px solid ${LINE}`,
                      boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 1,
                    maxWidth: 400,
                  }}
                >
                  {data.statuses.map((s) => (
                    <Box key={s.status} sx={innerCardStyle}>
                      <Typography variant="caption" sx={{ fontFamily: FONT, fontSize: 12, color: "#4A5A6E", lineHeight: 1.3 }}>{s.status}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: INK }}>{s.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Popover>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* LEADS OVER TIME */}
      <Box
        sx={{
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          p: "14px 16px 12px",
        }}
      >
        <Typography
          variant="subtitle1"
          mb={1.5}
          sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: INK }}
        >
          Leads Over Time
        </Typography>

        <Box height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF1F5" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.split("T")[0]}
                tick={{ fontFamily: MONO, fontSize: 11, fill: MUTED }}
                axisLine={{ stroke: LINE }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontFamily: MONO, fontSize: 11, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip
                labelFormatter={(label) => label.split("T")[0]}
                contentStyle={{ fontFamily: FONT, fontSize: 12.5, borderRadius: 10, border: `1px solid ${LINE}`, boxShadow: "0 12px 28px -12px rgba(15,27,42,0.3)" }}
              />
              <Line type="monotone" dataKey="count" stroke="#1F6FEB" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

const cardStyle = {
  border: "1px solid #E4E9EF",
  p: 0,
  borderRadius: "14px",
  minWidth: 160,
  textAlign: "left",
  backgroundColor: "#fff",
  boxShadow: "none",
  transition: "transform 120ms ease, box-shadow 120ms ease",
  "&:hover": { boxShadow: "0 10px 24px -14px rgba(15,27,42,0.35)" },
};
const cardContentSx = { p: "14px 16px !important" };
const capSx = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  lineHeight: 1.3,
};
const numSx = {
  mt: 0.5,
  fontFamily: MONO,
  fontSize: 30,
  fontWeight: 600,
  letterSpacing: "-0.03em",
  lineHeight: 1.1,
};

const innerCardStyle = {
  border: "1px solid #E4E9EF",
  borderRadius: "10px",
  p: 1.25,
  textAlign: "left",
  backgroundColor: "#F7F9FC",
};

export default MarketerDashboard;
